/**
 * DocumentRequirementsGrid
 * Extracted from WorkflowPhaseManager - displays and manages document requirements for a workflow phase
 * Broken into sub-components for maintainability
 */

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Edit, Eye, Download, Send, Mail, RotateCcw, Circle } from "lucide-react";
import { getDocumentSignedUrl } from "@/lib/storageUtils";
import { SignatureStatusBadge } from "@/components/signatures/SignatureStatusBadge";
import type {
  GeneratedDocument,
  SignatureRequest,
  DocumentEmailLog,
  DocumentTemplate,
  DocumentRequirement,
  DocumentStatus
} from "./types/workflow-components.types";

// ============= SUB-COMPONENTS =============

interface DocumentCardProps {
  templateKey: string;
  templateName: string;
  docStatus: DocumentStatus;
  generatedDoc?: GeneratedDocument;
}

const DocumentCard = ({ templateKey, templateName, docStatus, generatedDoc }: DocumentCardProps) => {
  return (
    <div className="border rounded-lg p-3 space-y-2 bg-card">
      {/* Document Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1">
          <docStatus.icon className={cn("h-4 w-4 mt-0.5", docStatus.color)} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{templateName}</p>
            <p className={cn("text-xs", docStatus.color)}>{docStatus.label}</p>
            {generatedDoc && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Created {new Date(generatedDoc.generated_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface DocumentActionButtonsProps {
  generatedDoc?: GeneratedDocument;
  docStatus: DocumentStatus;
  template?: DocumentTemplate;
  loading: boolean;
  onGenerate: () => void;
  onEdit: () => void;
  onPreview: () => void;
  onDownload: () => void;
  onRequestSignatures: () => void;
  onEmailDocument: () => void;
  onResend: () => void;
  onViewSignatureStatus: () => void;
}

const DocumentActionButtons = ({
  generatedDoc,
  docStatus,
  template,
  loading,
  onGenerate,
  onEdit,
  onPreview,
  onDownload,
  onRequestSignatures,
  onEmailDocument,
  onResend,
  onViewSignatureStatus
}: DocumentActionButtonsProps) => {
  return (
    <div className="flex gap-1.5 flex-wrap justify-end">
      {/* State 1: Not Generated */}
      {!generatedDoc && (
        <Button
          size="sm"
          onClick={onGenerate}
          disabled={!template || loading}
        >
          {loading ? 'Generating...' : 'Generate'}
        </Button>
      )}
      
      {/* State 2: Generated (Draft) - Show Edit + Preview */}
      {generatedDoc && generatedDoc.status === 'draft' && (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            disabled={loading}
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onPreview}
            disabled={loading}
          >
            <Eye className="h-3 w-3 mr-1" />
            Preview
          </Button>
          <Button
            size="sm"
            variant="default"
            onClick={onDownload}
            disabled={loading}
          >
            <Download className="h-3 w-3 mr-1" />
            {loading ? 'Downloading...' : 'Download PDF'}
          </Button>
        </>
      )}
      
      {/* State 3: Finalized - Show Edit, Preview, Download, and Request Signatures OR Email Document */}
      {generatedDoc && generatedDoc.status === 'final' && !docStatus.signatureData && !docStatus.emailData && (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            disabled={loading}
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onPreview}
            disabled={loading}
          >
            <Eye className="h-3 w-3 mr-1" />
            Preview PDF
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDownload}
            disabled={loading}
          >
            <Download className="h-3 w-3 mr-1" />
            {loading ? 'Downloading...' : 'Download PDF'}
          </Button>
          {docStatus.canRequestSignatures && (
            <Button
              size="sm"
              onClick={onRequestSignatures}
              className="gap-1"
              disabled={loading}
            >
              <Send className="h-3 w-3" />
              {loading ? 'Sending...' : 'Request Signatures'}
            </Button>
          )}
          {docStatus.canEmailDocument && (
            <Button
              size="sm"
              onClick={onEmailDocument}
              className="gap-1"
              disabled={loading}
            >
              <Mail className="h-3 w-3" />
              {loading ? 'Sending...' : 'Email Document'}
            </Button>
          )}
        </>
      )}
      
      {/* State 3b: Document Emailed (for non-signature docs) */}
      {docStatus.status === 'emailed' && generatedDoc && (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const url = await getDocumentSignedUrl(generatedDoc);
              if (url) window.open(url, '_blank');
            }}
          >
            <Download className="h-3 w-3 mr-1" />
            Download
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onResend}
            disabled={loading}
          >
            <Mail className="h-3 w-3 mr-1" />
            {loading ? 'Resending...' : 'Resend'}
          </Button>
        </>
      )}
      
      {/* State 4: Signatures Pending */}
      {docStatus.signatureData && docStatus.signatureData.status !== 'completed' && (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={onResend}
            disabled={loading}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            {loading ? 'Re-sending...' : 'Re-send Request'}
          </Button>
          <Button
            size="sm"
            variant="link"
            onClick={onViewSignatureStatus}
            className="h-auto px-2 text-xs"
            disabled={loading}
          >
            View Status
          </Button>
        </>
      )}
      
      {/* State 5: Fully Signed */}
      {docStatus.status === 'fully_signed' && (
        <Button
          size="sm"
          variant="outline"
          onClick={onPreview}
          disabled={loading}
        >
          <Eye className="h-3 w-3 mr-1" />
          Preview
        </Button>
      )}
    </div>
  );
};

interface SignatureStatusSectionProps {
  docStatus: DocumentStatus;
  generatedDoc?: GeneratedDocument;
  onViewStatus: () => void;
}

const SignatureStatusSection = ({ docStatus, generatedDoc, onViewStatus }: SignatureStatusSectionProps) => {
  if (!docStatus.requiresSignatures || !docStatus.signatureData) return null;

  // Map database status to badge status
  const getBadgeStatus = (status: string): 'not_requested' | 'pending' | 'in_progress' | 'completed' => {
    if (status === 'completed') return 'completed';
    if (status === 'pending') {
      // If some signatures are collected, it's in_progress
      if (docStatus.signatureData && docStatus.signatureData.signed_count > 0) {
        return 'in_progress';
      }
      return 'pending';
    }
    return 'pending';
  };

  return (
    <div className="border-t pt-2 mt-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SignatureStatusBadge
            status={getBadgeStatus(docStatus.signatureData.status)}
            signedCount={docStatus.signatureData.signed_count}
            totalSigners={docStatus.signatureData.total_signers}
          />
          <span className="text-xs text-muted-foreground">
            {docStatus.signatureData.signed_count} of {docStatus.signatureData.total_signers} signed
          </span>
        </div>
        
        <Button
          size="sm"
          variant="link"
          onClick={onViewStatus}
          className="h-auto p-0 text-xs"
        >
          View Status
        </Button>
      </div>

      {/* Download Signed PDF (when completed) */}
      {docStatus.status === 'fully_signed' && generatedDoc?.signed_file_url && (
        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            const url = await getDocumentSignedUrl(generatedDoc);
            if (url) window.open(url, '_blank');
          }}
        >
          <Download className="h-3 w-3" />
          Download Signed Document
        </Button>
      )}
    </div>
  );
};

interface EmailDeliveryStatusSectionProps {
  docStatus: DocumentStatus;
}

const EmailDeliveryStatusSection = ({ docStatus }: EmailDeliveryStatusSectionProps) => {
  if (docStatus.requiresSignatures || !docStatus.emailData) return null;

  return (
    <div className="border-t pt-2 mt-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Mail className="h-3 w-3" />
        <span>Sent to {docStatus.emailData.recipient_email}</span>
        <span className="text-[10px]">
          {new Date(docStatus.emailData.sent_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
};

// ============= MAIN COMPONENT =============

export interface DocumentRequirementsGridProps {
  requirements: DocumentRequirement[];
  generatedDocuments: Record<string, GeneratedDocument>;
  signatureRequests: Record<string, SignatureRequest>;
  emailDeliveries: Record<string, DocumentEmailLog>;
  templatesByKey: Record<string, DocumentTemplate>;
  templateKeyToName: Record<string, string>;
  phaseNumber: number;
  loading: boolean;
  getDocumentStatus: (templateKey: string, phaseNumber: number) => DocumentStatus;
  onGenerateDocument: (templateId: string) => void;
  onEditDocument: (documentId: string) => void;
  onPreviewDocument: (documentId: string) => void;
  onDownloadDocument: (documentId: string, documentName: string) => void;
  onRequestSignatures: (documentId: string) => void;
  onEmailDocument: (documentId: string) => void;
  onResendSignatureRequest: (documentId: string, signatureRequestId: string) => void;
  onViewSignatureStatus: (signatureRequestId: string) => void;
}

export const DocumentRequirementsGrid = ({
  requirements,
  generatedDocuments,
  signatureRequests,
  emailDeliveries,
  templatesByKey,
  templateKeyToName,
  phaseNumber,
  loading,
  getDocumentStatus,
  onGenerateDocument,
  onEditDocument,
  onPreviewDocument,
  onDownloadDocument,
  onRequestSignatures,
  onEmailDocument,
  onResendSignatureRequest,
  onViewSignatureStatus
}: DocumentRequirementsGridProps) => {
  // Helper to get requirement key
  const getReqKey = (req: string | DocumentRequirement): string => {
    return typeof req === 'string' ? req : req.template_key;
  };

  // Helper to get requirement name
  const getReqName = (req: string | DocumentRequirement): string => {
    if (typeof req === 'object' && req.document_name) {
      return req.document_name;
    }
    const key = getReqKey(req);
    return templateKeyToName[key] || key;
  };

  return (
    <div className="space-y-3">
      {requirements.map((req, idx) => {
        const templateKey = getReqKey(req);
        const template = templatesByKey[templateKey];
        const docStatus = getDocumentStatus(templateKey, phaseNumber);
        const generatedDoc = generatedDocuments[templateKey];
        const templateName = getReqName(req);
        
        return (
          <div key={idx} className="border rounded-lg p-3 space-y-2 bg-card">
            {/* Document Header */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 flex-1">
                <docStatus.icon className={cn("h-4 w-4 mt-0.5", docStatus.color)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{templateName}</p>
                  <p className={cn("text-xs", docStatus.color)}>{docStatus.label}</p>
                  {generatedDoc && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Created {new Date(generatedDoc.generated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <DocumentActionButtons
                generatedDoc={generatedDoc}
                docStatus={docStatus}
                template={template}
                loading={loading}
                onGenerate={() => onGenerateDocument(template?.id || templateKey)}
                onEdit={() => generatedDoc && onEditDocument(generatedDoc.id)}
                onPreview={() => generatedDoc && onPreviewDocument(generatedDoc.id)}
                onDownload={() => generatedDoc && onDownloadDocument(generatedDoc.id, generatedDoc.document_name)}
                onRequestSignatures={() => generatedDoc && onRequestSignatures(generatedDoc.id)}
                onEmailDocument={() => generatedDoc && onEmailDocument(generatedDoc.id)}
                onResend={() => {
                  if (docStatus.signatureData && generatedDoc) {
                    onResendSignatureRequest(generatedDoc.id, docStatus.signatureData.id);
                  } else if (generatedDoc) {
                    onEmailDocument(generatedDoc.id);
                  }
                }}
                onViewSignatureStatus={() => docStatus.signatureData && onViewSignatureStatus(docStatus.signatureData.id)}
              />
            </div>

            {/* Signature Status Section */}
            <SignatureStatusSection
              docStatus={docStatus}
              generatedDoc={generatedDoc}
              onViewStatus={() => docStatus.signatureData && onViewSignatureStatus(docStatus.signatureData.id)}
            />

            {/* Email Delivery Status Section */}
            <EmailDeliveryStatusSection docStatus={docStatus} />
          </div>
        );
      })}
    </div>
  );
};
